import { ref, update } from "firebase/database";
import { type FormEvent, type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { getFirebase } from "../firebase/init.js";
import { type EditableCharacter, removeSkill, setSkillRank } from "../lib/character-edits.js";

interface Props {
  characterPath: string;
  character: EditableCharacter;
}

export default function CharacterSkillsEditor({ characterPath, character }: Props): ReactElement {
  const { t } = useTranslation();
  const [newId, setNewId] = useState("");
  const [newRank, setNewRank] = useState("1");

  async function adjust(skillId: string, delta: number): Promise<void> {
    const current = character.skills?.[skillId] ?? 0;
    const { db } = getFirebase();
    await update(ref(db), setSkillRank(characterPath, character, skillId, current + delta));
  }

  async function handleRemove(skillId: string): Promise<void> {
    if (!window.confirm(t("edit.confirm.delete-skill"))) return;
    const { db } = getFirebase();
    await update(ref(db), removeSkill(characterPath, skillId));
  }

  async function handleAdd(e: FormEvent): Promise<void> {
    e.preventDefault();
    const id = newId.trim().toLowerCase();
    if (id.length === 0) return;
    const rank = Number.parseInt(newRank, 10);
    if (Number.isNaN(rank)) return;
    const { db } = getFirebase();
    await update(ref(db), setSkillRank(characterPath, character, id, rank));
    setNewId("");
    setNewRank("1");
  }

  const skills = Object.entries(character.skills ?? {}).sort(([, a], [, b]) => b - a);

  return (
    <section>
      <h2 className="text-sm font-semibold uppercase text-muted-foreground">{t("sheet.skills")}</h2>
      <ul className="mt-2 space-y-1 font-mono text-sm">
        {skills.map(([id, rank]) => (
          <li key={id} className="flex items-center gap-2">
            <span className="flex-1">{id}</span>
            <button
              type="button"
              aria-label={`${t("edit.skill.decrement")} ${id}`}
              className="rounded border px-2 py-0.5"
              onClick={() => adjust(id, -1)}
            >
              −
            </button>
            <span className="w-6 text-center">{rank >= 0 ? `+${rank}` : rank}</span>
            <button
              type="button"
              aria-label={`${t("edit.skill.increment")} ${id}`}
              className="rounded border px-2 py-0.5"
              onClick={() => adjust(id, +1)}
            >
              +
            </button>
            <button
              type="button"
              aria-label={`${t("edit.delete")} ${id}`}
              className="rounded border border-rose-400 px-2 py-0.5 text-rose-700"
              onClick={() => handleRemove(id)}
            >
              ×
            </button>
          </li>
        ))}
      </ul>

      <form onSubmit={handleAdd} className="mt-3 flex gap-2 text-sm">
        <input
          className="flex-1 rounded border px-2 py-1"
          placeholder={t("edit.skill.new-id-placeholder")}
          value={newId}
          onChange={(e) => setNewId(e.target.value)}
          aria-label={t("edit.skill.new-id")}
        />
        <input
          className="w-16 rounded border px-2 py-1 text-center"
          type="number"
          min={-2}
          max={10}
          value={newRank}
          onChange={(e) => setNewRank(e.target.value)}
          aria-label={t("edit.skill.new-rank")}
        />
        <button type="submit" className="rounded border px-2 py-1">
          {t("edit.skill.add")}
        </button>
      </form>
    </section>
  );
}
